import { useEffect, useState } from "react";
import API from "@/lib/api";
import { DollarSign, TrendingUp, Sparkles, PackageCheck, Clock } from "lucide-react";
import HelpLink from "@/components/HelpLink";

/**
 * Prescriber's personal "My Commission" widget — scoped to /api/prescriber/my-commission.
 * Shows the same shape as the admin Efficiency widget but for ME only.
 *
 * Preview-only. Real payroll runs through the existing comp process.
 */
export default function MyCommissionCard() {
  const [data, setData] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    API.get(`/prescriber/my-commission?days=${windowDays}`)
      .then(({ data }) => alive && setData(data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [windowDays]);

  const totals = data?.totals || { series_generated: 0, shipments_queued: 0, shipments_released: 0, projected_comp_usd: 0, shipments_per_pay_event: 0, series_pre_screened: 0, series_standard: 0 };
  const rates = data?.commission_rates || { monthly_usd: 50, royal_usd: 75, pre_screened_usd: 50 };

  return (
    <div
      className="bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/30 dark:to-slate-900 rounded-2xl border border-emerald-200 dark:border-emerald-900/50 p-5 shadow-sm"
      data-testid="my-commission-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center shadow-sm">
            <DollarSign className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white text-base inline-flex items-center gap-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              My commission this month
              <HelpLink sectionId="rx-my-commission" title="Training: My Commission widget & pay rates" />
            </h3>
            <p className="text-[11px] text-slate-500">Preview · Payroll runs through your existing comp process</p>
          </div>
        </div>
        <select
          value={windowDays}
          onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
          className="text-xs border border-emerald-200 dark:border-emerald-800 rounded-lg px-2 py-1 bg-white dark:bg-slate-800"
          data-testid="my-commission-window-select"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Headline number */}
      <div className="text-center py-4">
        <p className="text-[11px] uppercase tracking-wider text-emerald-700/70 dark:text-emerald-300/70 font-semibold">
          Projected paycheck
        </p>
        <p
          className="text-5xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums tracking-tight"
          style={{ fontFamily: "'Outfit', sans-serif" }}
          data-testid="my-commission-amount"
        >
          {loading ? "…" : `$${(totals.projected_comp_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        </p>
        <p className="text-[11px] text-slate-500 mt-1">
          From <b>{totals.series_generated}</b> series · Monthly ${rates.monthly_usd?.toFixed(2)} · Royal ${rates.royal_usd?.toFixed(2)} · Pre-Screened ${rates.pre_screened_usd?.toFixed(2)}
        </p>
      </div>

      {/* Mini stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
        <MiniStat icon={Sparkles} label="Series" value={totals.series_generated} />
        <MiniStat icon={Sparkles} label="Pre-Scr." value={totals.series_pre_screened || 0} tone="purple" />
        <MiniStat icon={Clock} label="Queued" value={totals.shipments_queued} />
        <MiniStat icon={PackageCheck} label="Released" value={totals.shipments_released} />
        <MiniStat icon={TrendingUp} label="Ship/pay" value={`${totals.shipments_per_pay_event || 0}×`} />
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, tone }) {
  // Pre-screened tone uses purple for visual differentiation from the
  // default emerald (matches the "PS" badge color in the order queue).
  const iconCls = tone === "purple"
    ? "text-purple-600 dark:text-purple-400"
    : "text-emerald-600 dark:text-emerald-400";
  return (
    <div className="bg-white/70 dark:bg-slate-800/40 rounded-lg px-2.5 py-1.5 text-center">
      <div className="flex items-center justify-center gap-1 mb-0.5">
        <Icon className={`w-3 h-3 ${iconCls}`} />
        <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      </div>
      <p className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">{value}</p>
    </div>
  );
}
